/* Y3 · the account surfaces' shared styles, now expressed in the design
 * system's own tokens rather than a second set of values.
 *
 * These five exports were the app's OTHER button system: a 6px radius, a 13px
 * label and a hardcoded white, drawn before theme.js had `PRIMARY`/`SECONDARY`.
 * App.jsx, AuthPanel.jsx, ProModal.jsx and DeleteAccount.jsx all spread them,
 * so a sign-in button and a drill button looked like two different apps.
 * Rewriting them here fixes all four files without touching a call site.
 *
 * `authLink` deliberately stays a plain inline text style — not `GHOST`. It is
 * used INSIDE running paragraphs (the privacy policy's "Terms of Service", the
 * delete-account page's "Contact"), and `GHOST`'s inline-flex box with a 40px
 * minimum height would break those sentences apart. Standalone text actions
 * should reach for `GHOST` directly instead.
 */
import { C, GHOST, PRIMARY_COMPACT, R, SECONDARY_COMPACT, T, TONES } from "./theme.js";
import { t } from "./i18n.js";

/* Firebase error code to a sentence a person can act on. The map holds KEYS
 * rather than sentences: it is a module-level constant, so a resolved string
 * would freeze at whatever the locale was when this file was first imported. */
const AUTH_ERROR_KEYS = {
  "auth/email-already-in-use": "auth.errors.emailInUse",
  "auth/invalid-email": "auth.errors.invalidEmail",
  "auth/invalid-credential": "auth.errors.invalidCredential",
  "auth/weak-password": "auth.errors.weakPassword",
  "auth/too-many-requests": "auth.errors.tooManyRequests",
};

export const authErrorMessage = (error) => t(AUTH_ERROR_KEYS[error] || "auth.errors.generic");

/* The one text field: a button's shape and height, so a form reads as a stack
 * of same-sized controls rather than short inputs above tall buttons. */
export const authInput = {
  ...T.body,
  background: C.cardDeep,
  border: `1.5px solid ${C.edge}`,
  borderRadius: R.md,
  boxSizing: "border-box",
  color: C.ink,
  minHeight: 46,
  outline: "none",
  padding: "11px 12px",
  width: "100%",
};

/* No `width` on either button: they are used both full-width inside a grid
 * form and inline beside other content (Settings' Sign in). */
export const authPrimary = { ...PRIMARY_COMPACT, padding: "0 16px" };
export const authSecondary = { ...SECONDARY_COMPACT, padding: "0 16px" };

/* "Continue without an account" — always standalone, so it can be a real
 * ghost action with a proper touch target. */
export const authGuest = { ...GHOST, color: C.sub };

/* See the header note: inline-safe on purpose. */
export const authLink = { ...T.small, background: "transparent", border: "none", color: C.sub, cursor: "pointer", padding: 4 };

export const authError = {
  ...T.caption,
  background: TONES.danger.ground,
  border: `1px solid ${TONES.danger.edge}`,
  borderRadius: R.md,
  color: TONES.danger.ink,
  padding: "9px 11px",
};
